/**
 * Request Info Section Component
 *
 * @module @features/requests/components/RequestDetails/components/RequestInfo
 */

import React from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { useI18n, useRequestTranslations } from '@shared/i18n';
import type { Request } from '@features/requests/types';
import { RequestSource } from '@features/requests/types';
import styles from '../RequestDetails.module.css';

/**
 * Props for RequestInfo component
 */
export interface RequestInfoProps {
    request: Request;
}

/**
 * Displays request type and shift preference information
 */
export function RequestInfo({ request }: RequestInfoProps): React.ReactElement {
    const { t } = useI18n();
    const { getShiftPreferenceLabel } = useRequestTranslations();

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
                <InfoOutlined
                    fontSize="small"
                    className={styles.sectionIcon}
                />
                {t('requests.details.requestDetails')}
            </h3>
            <div className={styles.detail}>
                <span className={styles.detailLabel}>
                    {t('requests.details.type')}:{' '}
                </span>
                <span className={styles.detailValue}>{request.type}</span>
            </div>

            {request.shiftPreference && request.type !== RequestSource.WISH && (
                <div className={styles.detail}>
                    <span className={styles.detailLabel}>
                        {t('requests.details.shiftPreference')}:{' '}
                    </span>
                    <span className={styles.detailValue}>
                        {getShiftPreferenceLabel(request.shiftPreference)}
                    </span>
                </div>
            )}
        </div>
    );
}
